<template>
  <ValidationObserver :data-e2e="BillingCypressAttribute.checkoutSession.views.STANDARD_PLAN_FLOW">
    <SelfServeCheckoutSessionFlowCardSkeleton v-if="isLoading" />
    <ModalCard v-else :is-scrollable="false">
      <template #header>
        <div class="has-text-centered is-full-width">
          <div
            class="is-size-1"
            v-dompurify-html="header"
            :data-e2e="BillingCypressAttribute.checkoutSession.views.STANDARD_PLAN_FLOW_HEADER"
          />
          <div
            v-if="subheader"
            class="is-size-3 has-text-weight-medium pt-3"
            v-dompurify-html="subheader"
            :data-e2e="BillingCypressAttribute.checkoutSession.views.STANDARD_PLAN_FLOW_SUBHEADER"
          />
        </div>
      </template>
      <template #body>
        <VaultCheckoutSessionCard :billing-plan-form="billingPlanForm" />
      </template>
      <template #footer>
        <div class="is-full-width">
          <div class="is-flex is-justify-content-space-between is-full-width is-flex-wrap-wrap">
            <ButtonView
              type="is-primary is-outlined"
              @click="otherPlansAction"
              class="my-2"
              :data-e2e="BillingCypressAttribute.checkoutSession.buttons.SHOW_OTHER_PLANS"
            >
              <span class="whitespace-pre-line">{{ showOtherPlansText }}</span>
            </ButtonView>
            <ButtonView
              type="is-primary"
              @click="next"
              class="my-2"
              :data-e2e="ModalCypressAttribute.buttons.CONFIRM"
            >
              {{ $t('billing.continue-plan') }}
            </ButtonView>
          </div>
          <div class="is-full-width has-text-centered pt-5 is-size-5">
            {{ $t('billing.continuing-terms-above') }}
          </div>
          <div class="is-flex is-justify-content-space-between is-full-width pt-4">
            <TrustpilotWidget :widget-type="TrustPilotWidgetType.WIDGET_MICRO_STAR_ID" />
            <img class="stripe-box is-radius-medium" src="@/assets/logos/stripe.svg" alt="Stripe" />
          </div>
        </div>
      </template>
    </ModalCard>
  </ValidationObserver>
</template>

<script>
import ModalCard from '@/modules/core/ui/components/ModalCard.vue';
import { CheckoutSessionCart } from '@/modules/billing/models/CheckoutSessionCart';
import { ProductKey } from '@/modules/estate/enums/ProductKey';
import { HasCurrentEstateMixin } from '@/modules/core/app/mixins/HasCurrentEstateMixin';
import pluralize from 'pluralize';
import { FormFlowCompletionMixin } from '@/modules/core/form/mixins/FormFlowCompletionMixin';
import { ModalCypressAttribute } from '@/modules/core/app/constants/ModalCypressAttribute';
import { BillingCypressAttribute } from '@/modules/billing/constants/BillingCypressAttribute';
import SelfServeCheckoutSessionFlowCardSkeleton from '@/modules/billing/components/flow/standardplan/skeletons/SelfServeCheckoutSessionFlowCardSkeleton.vue';
import VaultCheckoutSessionCard from '@/modules/billing/components/checkout/presentational/VaultCheckoutSessionCard.vue';
import ModalService from '@/modules/core/ui/services/ModalService';
import SelfServeUpgradeModal from '@/modules/billing/components/checkout/stateful/SelfServeUpgradeModal.vue';
import { ModalMixin } from '@/modules/core/ui/mixins/ModalMixin';
import ButtonView from '@/modules/core/ui/components/wrappers/ButtonView.vue';
import { VaultBillingPlanFormFactory } from '@/modules/estate/models/VaultBillingPlanFormFactory';
import { EstateConsent } from '@/modules/estate/models/EstateConsent';
import { EstateConsentType } from '@/modules/estate/enums/EstateConsentType';
import TrustpilotWidget from '@/modules/trustpilot/components/presentational/TrustPilotWidget.vue';
import { TrustPilotWidgetType } from '@/modules/trustpilot/constants/TrustPilotWidgetType';
import { HasStandardPlanOnboardingTrackingSegmentMixin } from '@/integrations/segment/mixins/HasStandardPlanOnboardingTrackingSegmentMixin';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'SelfServeCheckoutSessionFlow_1',
  emits: ['setStyleOverrides'],
  mixins: [
    HasCurrentEstateMixin,
    FormFlowCompletionMixin,
    ModalMixin,
    HasStandardPlanOnboardingTrackingSegmentMixin
  ],
  components: {
    TrustpilotWidget,
    ButtonView,
    VaultCheckoutSessionCard,
    SelfServeCheckoutSessionFlowCardSkeleton,
    ModalCard
  },
  props: {
    value: {
      type: CheckoutSessionCart
    }
  },
  static: {
    ProductKey,
    modalOptions: {
      canCancel: true,
      width: '72%'
    },
    TrustPilotWidgetType,
    BillingCypressAttribute,
    ModalCypressAttribute
  },
  data() {
    return {
      isLoading: true,
      billingPlanForm: null
    };
  },
  computed: {
    header() {
      return this.service.getHeaderText(this.currentEstate.billing);
    },
    subheader() {
      return this.service.getSubheaderText();
    },
    showOtherPlansText() {
      return this.currentEstate.billing?.isBasicPlan
        ? this.$t('billing.learn-other-plans')
        : this.$t('billing.talk-to-us-learn-other-plans');
    }
  },
  methods: {
    async loadProducts() {
      this.billingPlanForm = VaultBillingPlanFormFactory.build(this.service?.billingPlanId);

      try {
        this.isLoading = true;

        await this.billingPlanForm.loadPrices();
      } finally {
        this.isLoading = false;
      }
    },
    pluralize,
    otherPlansAction() {
      this.dispatchPaywallPaymentModalUpgradeScreenSelectedEventTracking(window.location.href);
      this.close();
      ModalService.show({
        parent: this,
        component: SelfServeUpgradeModal,
        fullScreen: true
      });
    },
    next() {
      this.dispatchPaywallPaymentModalCompletedEventTracking(window.location.href);
      this.form.productKeys = [
        this.billingPlanForm.productKey,
        this.billingPlanForm.chargeRecurringFee
          ? this.billingPlanForm.recurringProductKey
          : undefined,
        ...this.form.productKeys
      ].filter(Boolean);

      this.form.successUrl = this.form.successUrl ?? window.location.href;

      this.form.consentToPlanningPurchaseTerms = EstateConsent.mapModel({
        type: EstateConsentType.CONSENT_TO_PLANNING_PURCHASE_TERMS,
        value: true
      });

      this.submit();
    }
  },
  mounted() {
    this.$emit('setStyleOverrides', { 'margin-top': 0 });
    this.dispatchPaywallPaymentModalViewedEventTracking(window.location.href);

    this.loadProducts();
  }
};
</script>

<style scoped>
.stripe-box {
  border: 1px solid var(--ce-stripe-blurple);
  height: var(--ce-50px);
}
</style>
